/**
 * TankHistoryModal.jsx  v2
 * Usa RangoFechas — default mes actual
 */

import { useState, useEffect, useCallback } from 'react';
import {
    Box, Button, CircularProgress, Chip,
    Dialog, DialogContent, DialogTitle,
    Grid, IconButton, Paper, Stack, Tab, Tabs,
    Typography, alpha, useTheme,
} from '@mui/material';
import {
    Close as CloseIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    BarChart as BarChartIcon,
    List as ListIcon,
    CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { LineChart } from '@mui/x-charts/LineChart';
import RangoFechas, { mesActual } from './Rangofechas';

export default function TankHistoryModal({ open, tank, cfg, apiUrl, onClose }) {
    const theme = useTheme();
    const [tab, setTab] = useState(0);
    const [rango, setRango] = useState(() => mesActual());
    const [eventos, setEventos] = useState([]);
    const [historico, setHistorico] = useState([]);
    const [loadingEv, setLoadingEv] = useState(false);
    const [loadingHis, setLoadingHis] = useState(false);
    const [resumenHoy, setResumenHoy] = useState(null);

    const headers = { 'x-api-key': process.env.REACT_APP_HMI_API_KEY || '' };

    // Cargar eventos
    useEffect(() => {
        if (!open || !tank) return;
        setLoadingEv(true);
        fetch(`${apiUrl}/api/alvear/eventos/${tank.id}?from=${encodeURIComponent(rango.from)}&to=${encodeURIComponent(rango.to)}`, { headers })
            .then((r) => r.json())
            .then((d) => setEventos(d.eventos || []))
            .catch(() => setEventos([]))
            .finally(() => setLoadingEv(false));
    }, [open, tank, rango, apiUrl]);

    // Cargar histórico de masa
    useEffect(() => {
        if (!open || !tank) return;
        setLoadingHis(true);
        // Calcular intervalo según duración del rango
        const dias = (new Date(rango.to) - new Date(rango.from)) / (1000 * 60 * 60 * 24);
        const intervalo = dias <= 1 ? 10 : dias <= 7 ? 60 : dias <= 30 ? 120 : 240;

        fetch(`${apiUrl}/api/alvear/historico-masa?from=${encodeURIComponent(rango.from)}&to=${encodeURIComponent(rango.to)}&intervalo=${intervalo}&limit=500`, { headers })
            .then((r) => r.json())
            .then((d) => {
                setHistorico(d);
                // Resumen: primer y último punto del rango
                const key = `TK_${tank.id}_tn`;
                const puntos = d.filter((p) => p[key] !== null);
                if (puntos.length > 1) {
                    const inicio = puntos[0][key];
                    const fin = puntos[puntos.length - 1][key];
                    setResumenHoy({ inicio, fin, delta: parseFloat((fin - inicio).toFixed(2)) });
                } else {
                    setResumenHoy(null);
                }
            })
            .catch(() => setHistorico([]))
            .finally(() => setLoadingHis(false));
    }, [open, tank, rango, apiUrl]);

    // Reset al abrir
    useEffect(() => {
        if (open) {
            setTab(0);
            setRango(mesActual());
        }
    }, [open]);

    if (!tank) return null;

    const nivelKey = `TK_${tank.id}_pct`;
    const tnKey = `TK_${tank.id}_tn`;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: 2, maxHeight: '92vh' } }}>

            {/* Header */}
            <DialogTitle sx={{ pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: tank.color, flexShrink: 0, mt: 0.5 }} />
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                                {cfg?.label || tank.label} — Historial
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                                {cfg?.producto || 'propano'} · {(cfg?.capacidad_lts || 0).toLocaleString('es-AR')} lts
                            </Typography>
                        </Box>
                    </Stack>
                    <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>

                {/* Selector de rango */}
                <Box sx={{ mt: 1.5 }}>
                    <RangoFechas value={rango} onChange={setRango} />
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}
                    sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Tab icon={<ListIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="Eventos" sx={{ fontSize: 12, minHeight: 42 }} />
                    <Tab icon={<BarChartIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="Nivel %" sx={{ fontSize: 12, minHeight: 42 }} />
                    <Tab icon={<CalendarIcon sx={{ fontSize: 15 }} />} iconPosition="start" label="Toneladas" sx={{ fontSize: 12, minHeight: 42 }} />
                </Tabs>

                <Box sx={{ p: 2, overflowY: 'auto', maxHeight: '55vh' }}>

                    {/* TAB 0 — EVENTOS */}
                    {tab === 0 && (
                        <>
                            {/* Balance del rango */}
                            {resumenHoy && (
                                <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                                    <Typography variant="caption" color="text.disabled"
                                        sx={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 1 }}>
                                        Balance del período
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {[
                                            { label: 'Inicio', val: `${resumenHoy.inicio} tn` },
                                            { label: 'Fin', val: `${resumenHoy.fin} tn` },
                                            {
                                                label: 'Variación', val: `${resumenHoy.delta > 0 ? '+' : ''}${resumenHoy.delta} tn`,
                                                color: resumenHoy.delta > 0 ? 'success.main' : resumenHoy.delta < 0 ? 'error.main' : 'text.primary'
                                            },
                                        ].map((s, i) => (
                                            <Grid item xs={4} key={i}>
                                                <Typography variant="caption" color="text.disabled" sx={{ fontSize: 9, display: 'block' }}>{s.label}</Typography>
                                                <Typography sx={{ fontFamily: "'Orbitron', sans-serif", fontSize: 15, fontWeight: 700, color: s.color || 'text.primary' }}>
                                                    {s.val}
                                                </Typography>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Paper>
                            )}

                            {loadingEv ? (
                                <Stack alignItems="center" py={4}>
                                    <CircularProgress size={28} />
                                    <Typography variant="caption" color="text.disabled" mt={1}>Detectando eventos...</Typography>
                                </Stack>
                            ) : eventos.length === 0 ? (
                                <Stack alignItems="center" py={4}>
                                    <Typography variant="body2" color="text.disabled">Sin eventos detectados en el período</Typography>
                                </Stack>
                            ) : (
                                <Stack spacing={1}>
                                    {eventos.map((ev, i) => (
                                        <Paper key={i} variant="outlined" sx={{
                                            p: 1.5,
                                            borderColor: ev.tipo === 'carga'
                                                ? alpha(theme.palette.success.main, 0.3)
                                                : alpha(theme.palette.error.main, 0.3),
                                        }}>
                                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                                    {ev.tipo === 'carga'
                                                        ? <TrendingUpIcon sx={{ color: 'success.main', fontSize: 20 }} />
                                                        : <TrendingDownIcon sx={{ color: 'error.main', fontSize: 20 }} />}
                                                    <Box>
                                                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                                            <Chip size="small"
                                                                label={ev.tipo === 'carga' ? 'CARGA' : 'DESCARGA'}
                                                                sx={{
                                                                    height: 20, fontSize: 9, fontWeight: 700,
                                                                    bgcolor: ev.tipo === 'carga'
                                                                        ? alpha(theme.palette.success.main, 0.12)
                                                                        : alpha(theme.palette.error.main, 0.12),
                                                                    color: ev.tipo === 'carga' ? 'success.main' : 'error.main',
                                                                    '& .MuiChip-label': { px: 1 },
                                                                }}
                                                            />
                                                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
                                                                {ev.fechaInicio} → {ev.fechaFin} · {ev.duracion}
                                                            </Typography>
                                                        </Stack>
                                                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
                                                            Nivel: {ev.nivelInicio}% → {ev.nivelFin}%
                                                            ({ev.deltaPct > 0 ? '+' : ''}{ev.deltaPct}%)
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                                    <Typography sx={{
                                                        fontFamily: "'Orbitron', sans-serif", fontSize: 18, fontWeight: 700,
                                                        color: ev.tipo === 'carga' ? 'success.main' : 'error.main', lineHeight: 1,
                                                    }}>
                                                        {ev.tipo === 'carga' ? '+' : '-'}{ev.deltaTn}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10 }}>tn</Typography>
                                                </Box>
                                            </Stack>
                                        </Paper>
                                    ))}
                                </Stack>
                            )}
                        </>
                    )}

                    {/* TAB 1 — NIVEL % */}
                    {tab === 1 && (
                        loadingHis ? (
                            <Stack alignItems="center" py={4}><CircularProgress size={28} /></Stack>
                        ) : (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600} fontSize={11} display="block" mb={1}>
                                    Nivel %
                                </Typography>
                                <LineChart
                                    dataset={historico.filter((p) => p[nivelKey] !== null)}
                                    series={[{ dataKey: nivelKey, label: cfg?.label || tank.label, color: tank.color, showMark: false, curve: 'monotoneX' }]}
                                    xAxis={[{
                                        dataKey: 'fecha', scaleType: 'band', tickLabelStyle: { fontSize: 8 },
                                        tickInterval: (_, i) => i % Math.ceil(historico.length / 8) === 0
                                    }]}
                                    yAxis={[{ min: 0, max: 100, tickLabelStyle: { fontSize: 9 }, label: '%' }]}
                                    height={280}
                                    margin={{ left: 40, right: 16, top: 8, bottom: 32 }}
                                    slotProps={{ legend: { hidden: true } }}
                                />
                            </Paper>
                        )
                    )}

                    {/* TAB 2 — TONELADAS */}
                    {tab === 2 && (
                        loadingHis ? (
                            <Stack alignItems="center" py={4}><CircularProgress size={28} /></Stack>
                        ) : (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600} fontSize={11} display="block" mb={1}>
                                    Toneladas disponibles
                                </Typography>
                                <LineChart
                                    dataset={historico.filter((p) => p[tnKey] !== null)}
                                    series={[{ dataKey: tnKey, label: `${cfg?.label || tank.label} (tn)`, color: tank.color, showMark: false, curve: 'monotoneX' }]}
                                    xAxis={[{
                                        dataKey: 'fecha', scaleType: 'band', tickLabelStyle: { fontSize: 8 },
                                        tickInterval: (_, i) => i % Math.ceil(historico.length / 8) === 0
                                    }]}
                                    yAxis={[{ tickLabelStyle: { fontSize: 9 }, label: 'tn' }]}
                                    height={280}
                                    margin={{ left: 50, right: 16, top: 8, bottom: 32 }}
                                    slotProps={{ legend: { hidden: true } }}
                                />
                            </Paper>
                        )
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
}